import { Injectable, signal } from "@angular/core";
import { QuantitySnapshot } from "../../../models/service-quantity.model";

/**
 * Tracks WHICH Service quantities the author has unlocked for editing —
 * `displays[].value.unitOfMeasure.amount` on one price — and what each showed
 * when it was opened.
 *
 * It exists for the same reason {@link EnrichmentPriceLockStore} does, but
 * guards a different thing. The backend rebuilds a bundle display's amount
 * from the parent catalog on EVERY price save (`lockBundleDisplayAmounts`), so
 * an amount that merely rides along in the payload is overwritten. Only an
 * amount the author actually authored may be sent; everything else must leave
 * the key off the wire so the backend goes on deriving it. This store is what
 * tells the two apart.
 *
 * The snapshot lives HERE rather than in `EnrichmentPricing` for the reason
 * ADR 0001 gives about the lock itself: that component sits inside the edit
 * shell's `@switch`/`@case ('price-config')` and is destroyed on every tab
 * switch, while the unlock and the edited value live on. A component-local
 * snapshot would come back empty after a tab switch, so Cancel would silently
 * do nothing and re-lock on top of the typed figure — leaving a value the
 * author cannot see behind a disabled input for the next save to ship, which
 * is the precise failure "locked means showing what is stored" exists to
 * prevent.
 *
 * Keyed by price id then display NAME, never by array index: the backend joins
 * a display by its name (and `productOfferingRef.id`), and the displays array
 * is rebuilt on reorder — an index would drift onto a different service than
 * the one the author clicked. Nesting the two keys keeps them separate values
 * rather than one encoded string, so no display name can spoof another price.
 *
 * Provided in `EnrichmentEditPackage`'s `providers` (NOT root) so it dies with
 * the route, like the two stores beside it. A fresh edit session always starts
 * locked, which means "showing the figure the catalog derives".
 */
@Injectable()
export class EnrichmentDisplayQuantityLockStore {
  private readonly byPrice = signal<
    ReadonlyMap<string, ReadonlyMap<string, QuantitySnapshot>>
  >(new Map());

  /** Read-only signal of the unlocked quantities, price id → display name →
   *  snapshot. Read it (not {@link isUnlocked}) from a `computed` or template
   *  so the read is tracked. */
  readonly unlocked = this.byPrice.asReadonly();

  /** Untracked point read — for imperative callers such as the save
   *  coordinator. A template or `computed` goes through {@link unlocked}. */
  isUnlocked(
    priceId: string | undefined,
    displayName: string | undefined,
  ): boolean {
    if (!priceId || !displayName) return false;
    return this.byPrice().get(priceId)?.has(displayName) ?? false;
  }

  /** What this quantity showed when it was unlocked; `undefined` when it is
   *  not unlocked at all. */
  snapshot(
    priceId: string | undefined,
    displayName: string | undefined,
  ): QuantitySnapshot | undefined {
    if (!priceId || !displayName) return undefined;
    return this.byPrice().get(priceId)?.get(displayName);
  }

  /** Open one quantity, remembering what it showed. Re-unlocking an already
   *  open quantity keeps the ORIGINAL snapshot: Cancel must undo the whole
   *  edit, not just the part since the last click. */
  unlock(
    priceId: string | undefined,
    displayName: string | undefined,
    snapshot: QuantitySnapshot,
  ): void {
    if (!priceId || !displayName) return;
    if (this.byPrice().get(priceId)?.has(displayName)) return;
    const next = new Map(this.byPrice());
    const forPrice = new Map(next.get(priceId) ?? []);
    forPrice.set(displayName, snapshot);
    next.set(priceId, forPrice);
    this.byPrice.set(next);
  }

  lock(priceId: string | undefined, displayName: string | undefined): void {
    if (!priceId || !displayName) return;
    const current = this.byPrice().get(priceId);
    if (!current?.has(displayName)) return;
    const next = new Map(this.byPrice());
    const forPrice = new Map(current);
    forPrice.delete(displayName);
    if (forPrice.size === 0) next.delete(priceId);
    else next.set(priceId, forPrice);
    this.byPrice.set(next);
  }

  /** Re-lock every quantity on one price — what a successful save of that
   *  price does, mirroring the price lock's own re-lock. */
  lockPrice(priceId: string | undefined): void {
    if (!priceId || !this.byPrice().has(priceId)) return;
    const next = new Map(this.byPrice());
    next.delete(priceId);
    this.byPrice.set(next);
  }

  clear(): void {
    if (this.byPrice().size === 0) return;
    this.byPrice.set(new Map());
  }
}
